import React from "react";
import "string.format";
import HcsControl from "./hcs-control";
import MessageBox from "../messagebox";

export default class HcsNavi extends HcsControl {
    constructor(props) {
        super(props);

        this.state = {
            disabled: false
        };

        this.buttonClick = this.buttonClick.bind(this);
        this._buttonClassString = this._buttonClassString.bind(this);
        this._listItemClass = this._listItemClass.bind(this);
    }

    childClass() {
        return "hcs-navi";
    }

    /**
     * Enable all navi buttons
     */
    enable() {
        this.setState({
            disabled: false
        });
    }

    /**
     * Disable all navi buttons
     */
    disable() {
        this.setState({
            disabled: true
        });
    }

    // override from base class
    buttonClick(event, itemData) {
        let self = this;

        const { data, hcs } = this.props;

        switch(itemData.data.ADATA9) {
            case "BTN_NEXT":
            case "BTN_FINISH":
            case "BTN_ABORT":
                // Must disable button to avoid double-click events
                this.disable();

                // We wait until (running) ECS callback has finished.
                // This is necessary because:
                // When the user enters the last required value inside a text field and immediately presses the "Next" button,
                // the blur event of the text field triggers the ECS callback, but the button "Next" event tells us that the current
                // configuration step has not yet been finished. This requires that the user has to press "Next" a second time.
                //
                // To avoid this we first wait for the text field callback, then we check if the current configuration step is ready to finish.
                // If not we show the warning message, otherwise we trigger the "Next" callback.
                this.props.configWait(["setCfgValue", "setSubCfgValue"], this).then(() => {
                    let groupData = self.props.groupData;

                    if(!groupData.finished) {
                        let missingAttributes = groupData.attributes
                            .filter(a => a.data.CFG_CONTROL !== "CTRL_NAVI" && a.info.visible && a.info.required && !a.info.assigned);

                        let attributesRequired = "<ul>" + missingAttributes.map(a => `<li>${a.info.trans}</li>`).join("") + "</ul>";

                        hcs.api.messageBox.show({
                            modal: true,
                            classNames: "hcs-attributes-required",
                            title: hcs.t.ecs("ConfigGroupLocked_Title"),
                            text: hcs.t.ecs("ConfigGroupLocked_Message").format({ attributesRequired: attributesRequired }),
                            buttons: MessageBox.MB_OK,
                            buttonTexts: {
                                "ok": hcs.t.ecs("ConfigGroupLocked_Button")
                            },
                            buttonClassCategories: {
                                "ok": "hcs-secondary"
                            },
                            messageType: MessageBox.MB_INFO,
                            onOk: function() {
                                let scrollToItem = document.querySelector(`li[ecs-key='${missingAttributes[0].ident}']`)

                                scrollToItem.scrollIntoView(true);

                                this.close();

                                let focusTextBox = scrollToItem.querySelector("input");

                                if(focusTextBox) {
                                    focusTextBox.focus();
                                }
                            }
                        });

                        self.enable();
                        return;
                    }

                    this.props.onConfig("setCfgValue", data.ident, itemData.ident, {
                        attrChecked: true,
                        sender: itemData.data.ADATA9,
                        success: function() {
                            self.triggerConfigurator();

                            switch(itemData.data.ADATA9) {
                                case "BTN_NEXT":
                                    self.changeGroup("GOTO-NEXT");
                                    break;

                                case "BTN_FINISH":
                                    self.props.onConfig("exitCurrentCfgObject", null, null, { accept: true });
                                    break;

                                case "BTN_ABORT":
                                    self.props.onConfig("exitCurrentCfgObject", null, null, { accept: true });
                                    break;
                            }
                        }
                    });
                });
                break;

            case "BTN_BACK":
                this.changeGroup("GOTO-PREV");
                break;

            case "BTN_PRINT":
                hcs.api.printTrigger();
                break;
        }
    }

    triggerConfigurator() {
        super.triggerConfigurator();
    }

    // override from base class
    getCcaoData() {
        const { data } = this.props;

        if(data.values && data.values[0].data.CCAO_DATA) {
            try {
                return JSON.parse(data.values[0].data.CCAO_DATA);
            } catch(e) {
                console.error(`Error parsing CCAO_DATA object from ECS for attribute '${data.ident}': ${e.message}: ${value.data.CCAO_DATA}`);
            }
        }

        return null;
    }

    _buttonClassString(itemData) {
        const { data } = this.props;

        let buttonClasses = ["hcs-button", this._listItemClass(itemData)];

        switch(itemData.data.ADATA9) {
            case "BTN_NEXT":
                buttonClasses.push("hcs-secondary");

                if(data.info.focused) {
                    buttonClasses.push("hcs-focused");
                }
                break;

            case "BTN_BACK":
                buttonClasses.push("hcs-primary");
                break;

            case "BTN_FINISH":
                buttonClasses.push("hcs-secondary");
                break;

            case "BTN_ABORT":
                buttonClasses.push("hcs-primary");
                break;

            case "BTN_PRINT":
                buttonClasses.push("hcs-primary");
                break;
        }

        return buttonClasses.join(" ");
    }

    changeGroup(groupId) {
        this.props.onGroupChanged(groupId);
    }

    _listItemClass(itemData) {
        switch(itemData.data.ADATA9) {
            case "BTN_NEXT":
                return "hcs-next";

            case "BTN_BACK":
                return "hcs-back";

            case "BTN_FINISH":
                return "hcs-finish";

            case "BTN_ABORT":
                return "hcs-abort";

            case "BTN_PRINT":
                return "hcs-print";
        }
    }

    render() {
        const { data, groupData } = this.props;

        return super.render(
            <ul>
                {
                    data.values.filter(i => i.info.visible).map(itemData => {
                        return (
                            <li
                                key={`hcs-navi-button-${itemData.data.ADATA9}`}
                                className={this._listItemClass(itemData)}
                            >
                                <button
                                    type="button"
                                    className={this._buttonClassString(itemData)}
                                    onClick={(event) => { this.buttonClick(event, itemData); }}
                                    disabled={this.state.disabled}
                                    dangerouslySetInnerHTML={{ __html: itemData.info.trans }}
                                />
                            </li>);
                    }, this)
                }
            </ul>
        );
    }
}
